import { EMAIL_REGEX } from "@circle-react/components/OnboardingPage/helpers/constants";
import { debounce } from "lodash";
import { useCallback, useEffect, useMemo, useRef } from "react";
import { useFormContext } from "react-hook-form";

import type { CheckoutFormValuesProps } from "@/react/components/Paywalls/Checkout/types/CheckoutFormValuesProps";

import type { EmailVerificationStatus } from "../types";
import { extractErrorMessage } from "./useEmailVerificationMutations";

const DEFAULT_DEBOUNCE_MS = 800;
const CO_SUFFIX_DEBOUNCE_MS = 1500;

const isCoSuffix = (email: string) => email.toLowerCase().endsWith(".co");

interface UseEmailValidationProps {
  onEmailChange: (email: string) => void;
  onValidationError: (error: string) => void;
  onValidationStart: () => void;
  onValidationSuccess: (status: EmailVerificationStatus) => void;
  verifyEmail: (email: string) => Promise<{ status: EmailVerificationStatus }>;
}

export const useEmailValidation = ({
  verifyEmail,
  onEmailChange,
  onValidationStart,
  onValidationSuccess,
  onValidationError,
}: UseEmailValidationProps) => {
  const { watch } = useFormContext<Pick<CheckoutFormValuesProps, "email">>();
  const formEmailValue = watch("email") || "";
  const requestGenerationRef = useRef(0);
  const isMountedRef = useRef(true);

  const validateEmail = useCallback(
    async (email: string) => {
      if (!EMAIL_REGEX.test(email)) return;

      const requestGeneration = requestGenerationRef.current;
      onValidationStart();
      try {
        const result = await verifyEmail(email);
        if (
          isMountedRef.current &&
          requestGeneration === requestGenerationRef.current
        ) {
          onValidationSuccess(result.status);
        }
      } catch (error) {
        if (
          isMountedRef.current &&
          requestGeneration === requestGenerationRef.current
        ) {
          onValidationError(extractErrorMessage(error));
        }
      }
    },
    [onValidationError, onValidationStart, onValidationSuccess, verifyEmail],
  );

  const debouncedValidateEmail = useMemo(
    () => debounce(validateEmail, DEFAULT_DEBOUNCE_MS),
    [validateEmail],
  );
  const debouncedValidateCoEmail = useMemo(
    () => debounce(validateEmail, CO_SUFFIX_DEBOUNCE_MS),
    [validateEmail],
  );
  const debouncedValidateEmailRef = useRef(debouncedValidateEmail);
  const debouncedValidateCoEmailRef = useRef(debouncedValidateCoEmail);

  useEffect(() => {
    debouncedValidateEmailRef.current = debouncedValidateEmail;
    debouncedValidateCoEmailRef.current = debouncedValidateCoEmail;
  }, [debouncedValidateCoEmail, debouncedValidateEmail]);

  const handleEmailChange = useCallback(
    (email: string) => {
      requestGenerationRef.current += 1;
      onEmailChange(email);
      debouncedValidateEmail.cancel();
      debouncedValidateCoEmail.cancel();

      if (EMAIL_REGEX.test(email)) {
        void (
          isCoSuffix(email) ? debouncedValidateCoEmail : debouncedValidateEmail
        )(email);
      }
    },
    [onEmailChange, debouncedValidateCoEmail, debouncedValidateEmail],
  );

  useEffect(() => {
    handleEmailChange(formEmailValue.trim());
  }, [formEmailValue, handleEmailChange]);

  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
      requestGenerationRef.current += 1;
      debouncedValidateEmailRef.current.cancel();
      debouncedValidateCoEmailRef.current.cancel();
    };
  }, []);

  return {
    handleEmailChange,
  };
};
